import React from 'react';
import { Link } from 'react-router-dom';
import SeoPanel from './SeoPanel';

/** Visible FAQ entries on the home page – also emitted as FAQPage structured data. */
export const HOME_FAQS = [
  {
    question: 'Is the platform available for players in Bangladesh?',
    answer:
      'Yes. The platform is built for players in Bangladesh: accounts run on Bangladeshi Taka (BDT) and deposits and withdrawals are supported through local mobile wallets and bank transfer.',
  },
  {
    question: 'How do I deposit money with bKash or Nagad?',
    answer:
      'Log in, open the Deposit page, pick the payment method you want to use such as bKash, Nagad or Rocket, enter the amount and confirm. The balance is added to the same wallet used for sports and casino games.',
  },
  {
    question: 'How long do withdrawals take?',
    answer:
      'Withdrawal requests are reviewed and processed as quickly as possible. The exact time depends on the payment provider and on any security or verification checks that may be required.',
  },
  {
    question: 'Can I play on a mobile phone?',
    answer:
      'Yes. The site is mobile first and works in the browser on Android and iOS, and a dedicated Android app is available for faster loading on slower connections.',
  },
  {
    question: 'How do I claim a welcome bonus or promotion?',
    answer:
      'Open the Promotions page, read the rules of the offer, then apply it before or after you deposit as described in the promotion details. Bonuses usually carry turnover requirements that must be met before withdrawal.',
  },
];

const HomeSeoContent = ({ brand = 'Our platform' }) => (
  <SeoPanel
    id="seo-home"
    preview={
      <>
        <h1 className="seo-h1" id="seo-home-title">
          {brand} – Online Cricket Betting, Live Casino &amp; Slot Games in Bangladesh
        </h1>

        <p className="seo-text">
          {brand} is an online betting and gaming platform made for players in Bangladesh. Bet on
          live cricket, football and other sports, take a seat at live casino tables such as Teen
          Patti, Andar Bahar, Roulette and Blackjack, and spin hundreds of slots, crash and fishing
          games — all from one account, on your phone or your computer.
        </p>
      </>
    }
  >
    <p className="seo-text">
      Every deposit and withdrawal is handled in Bangladeshi Taka (৳) through the payment channels
      players already use, so you can add funds in minutes, claim a bonus and start playing right
      away.
    </p>

    <h2 className="seo-h2">Popular ways to play at {brand}</h2>
    <ul className="seo-list">
      <li>
        <strong>Cricket betting:</strong> pre-match and live in-play markets for international
        fixtures, franchise leagues and domestic matches.
      </li>
      <li>
        <strong>Football and other sports:</strong> Asian handicaps, over/under, correct score and
        outright markets on the biggest leagues and tournaments.
      </li>
      <li>
        <strong>Live casino:</strong> real dealers streamed around the clock in Teen Patti, Andar
        Bahar, Roulette, Baccarat, Blackjack and Dragon Tiger.
      </li>
      <li>
        <strong>Slots and arcade games:</strong> hundreds of titles from leading international
        providers, from classic three-reel slots to modern video slots with free spins.
      </li>
      <li>
        <strong>Crash, fishing and instant games:</strong> fast rounds with quick results, plus
        spin-to-win style games for short sessions.
      </li>
      <li>
        <strong>Table games:</strong> a full lobby of card and board classics for players who
        prefer skill over luck.
      </li>
    </ul>

    <h2 className="seo-h2">Fast deposits and withdrawals in BDT</h2>
    <p className="seo-text">
      You can fund your account with the mobile financial services used across Bangladesh — bKash,
      Nagad, Rocket, Upay, OK Wallet, SureCash and Tap — plus bank transfer and cryptocurrency
      where available. Withdrawals are requested from your account page in BDT and paid back to the
      same channel once any required verification checks are completed.
    </p>

    <h2 className="seo-h2">How to start playing in three steps</h2>
    <ul className="seo-list">
      <li>
        <strong>Register:</strong> create an account with your mobile number and a password on the{' '}
        <Link to="/signup">sign up page</Link>.
      </li>
      <li>
        <strong>Deposit:</strong> choose a payment method such as bKash, Nagad or Rocket, enter the
        amount in BDT and confirm the payment.
      </li>
      <li>
        <strong>Play:</strong> browse the sportsbook, the live casino or the slots lobby on the{' '}
        <Link to="/games">games page</Link>, and remember to check the{' '}
        <Link to="/promotion">promotions page</Link> for bonus offers.
      </li>
    </ul>

    <h2 className="seo-h2">Bonuses, rebates and rewards</h2>
    <p className="seo-text">
      New players can claim a welcome offer on their first deposit, and regular players get weekly
      or monthly rebates on betting turnover, referral rewards when friends join, and extra perks
      through the loyalty programme. Promotions have their own rules, including eligibility,
      validity periods and turnover requirements, so always read the details before you apply.
    </p>

    <h2 className="seo-h2">Why players in Bangladesh choose {brand}</h2>
    <ul className="seo-list">
      <li>
        <strong>Local payments:</strong> deposit and withdraw in BDT with the mobile wallets you
        already have.
      </li>
      <li>
        <strong>One account:</strong> sportsbook, live casino, slots and promotions share a single
        wallet.
      </li>
      <li>
        <strong>Quick support:</strong> live chat and official social channels are available around
        the clock.
      </li>
      <li>
        <strong>Mobile first:</strong> the site is optimised for phones and slower mobile
        connections, and an Android app is available for one-tap access.
      </li>
      <li>
        <strong>Safe play:</strong> encrypted connections, account verification and monitoring
        tools help protect your balance and your personal details.
      </li>
    </ul>

    <h2 className="seo-h2">Questions players ask before signing up</h2>
    {HOME_FAQS.map((faq) => (
      <p className="seo-faq-item" key={faq.question}>
        <strong>{faq.question}</strong>
        <br />
        {faq.answer}
      </p>
    ))}
    <p className="seo-text">
      More answers about registration, passwords, deposits, withdrawals and support are on the{' '}
      <Link to="/faq">frequently asked questions page</Link>.
    </p>

    <h2 className="seo-h2">Helpful pages</h2>
    <ul className="seo-links">
      <li>
        <Link to="/promotion">Promotions and bonus offers</Link>
      </li>
      <li>
        <Link to="/games">All games and providers</Link>
      </li>
      <li>
        <Link to="/about-us">About us</Link>
      </li>
      <li>
        <Link to="/faq">FAQ</Link>
      </li>
      <li>
        <Link to="/contact-us">Contact us</Link>
      </li>
      <li>
        <Link to="/terms-and-conditions">Terms and conditions</Link>
      </li>
    </ul>

    <p className="seo-note">
      For adults only (18+). Never wager more than you can afford to lose, take regular breaks, and
      reach out to our support team if gambling stops being fun — account protection and
      responsible gaming tools are available to every player.
    </p>
  </SeoPanel>
);

export default HomeSeoContent;
